import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Outlet, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { mk, loadFont, themeVars } from "../../lib/marketApi";

const StoreCtx = createContext(null);
export const useStore = () => useContext(StoreCtx);

const readJSON = (k, d) => {
  try {
    return JSON.parse(localStorage.getItem(k)) ?? d;
  } catch {
    return d;
  }
};

export default function StoreShell() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [cart, setCart] = useState(() => readJSON(`mk_cart_${slug}`, []));
  const [buyer, setBuyer] = useState(() => readJSON(`mk_buyer_${slug}`, null));
  const [authOpen, setAuthOpen] = useState(false);

  const reload = useCallback(
    () =>
      mk(`/s/${slug}`)
        .then(setData)
        .catch((e) => setError(e.message)),
    [slug]
  );
  useEffect(() => {
    reload();
  }, [reload]);
  useEffect(() => localStorage.setItem(`mk_cart_${slug}`, JSON.stringify(cart)), [cart, slug]);
  useEffect(() => {
    if (buyer) localStorage.setItem(`mk_buyer_${slug}`, JSON.stringify(buyer));
    else localStorage.removeItem(`mk_buyer_${slug}`);
  }, [buyer, slug]);
  useEffect(() => {
    if (!data) return;
    loadFont(data.store.theme?.font);
    document.title = data.store.name;
  }, [data]);
  useEffect(() => {
    if (!buyer?.token) return;
    mk(`/s/${slug}/buyer/me`, { token: buyer.token }).catch((e) => e.status === 401 && setBuyer(null));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const products = useMemo(() => data?.products || [], [data]);
  const cartLines = useMemo(
    () =>
      cart
        .map((c) => ({ ...c, product: products.find((p) => p.id === c.id) }))
        .filter((c) => c.product),
    [cart, products]
  );
  const subtotal = cartLines.reduce((s, c) => s + c.product.price * c.qty, 0);
  const count = cartLines.reduce((s, c) => s + c.qty, 0);

  const addToCart = (id, qty = 1) =>
    setCart((c) => {
      const stock = products.find((p) => p.id === id)?.stock ?? 99;
      const ex = c.find((x) => x.id === id);
      if (ex) return c.map((x) => (x.id === id ? { ...x, qty: Math.min(stock, x.qty + qty) } : x));
      return [...c, { id, qty: Math.min(stock, qty) }];
    });
  const setQty = (id, qty) => setCart((c) => (qty <= 0 ? c.filter((x) => x.id !== id) : c.map((x) => (x.id === id ? { ...x, qty } : x))));
  const clearCart = () => setCart([]);

  if (error)
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 text-center" data-testid="store-error">
        <p className="text-slate-500">{error}</p>
      </div>
    );
  if (!data)
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-7 w-7 animate-spin text-slate-400" />
      </div>
    );

  const value = {
    slug, store: data.store, products, reload, cart: cartLines, subtotal, count,
    addToCart, setQty, clearCart, buyer, setBuyer, authOpen, setAuthOpen,
  };
  return (
    <StoreCtx.Provider value={value}>
      <div className="mk-root min-h-screen" style={{ ...themeVars(data.store.theme), background: "var(--mk-bg)", color: "var(--mk-text)" }}>
        <Outlet />
      </div>
    </StoreCtx.Provider>
  );
}
